import type { ApiError } from "./errors";

/** OWN-89: a plan-gated call that fails without a recent click/keypress is background work. */
const USER_GESTURE_WINDOW_MS = 1500;

const FEATURE_NOT_IN_PLAN_CODE = "ENTITLEMENT_FEATURE_NOT_AVAILABLE";

let lastGestureAt = Number.NEGATIVE_INFINITY;

/** Records a user gesture; wired once from the app shell. Returns the unsubscribe. */
export function trackUserGestures(target: Window = window): () => void {
  const mark = () => {
    lastGestureAt = Date.now();
  };
  const events = ["pointerdown", "keydown", "touchstart"] as const;
  for (const name of events) target.addEventListener(name, mark, true);
  return () => {
    for (const name of events) target.removeEventListener(name, mark, true);
  };
}

export function markUserGestureForTest(at: number): void {
  lastGestureAt = at;
}

/**
 * True when a failed write must NOT toast: the plan says "feature not available" and the call
 * was not triggered by the user (page-load / auto calls). The upgrade message is shown only
 * when the user acts on the feature.
 */
export function isBackgroundPlanDenial(error: ApiError, now: number = Date.now()): boolean {
  if (error.code !== FEATURE_NOT_IN_PLAN_CODE) return false;
  return now - lastGestureAt > USER_GESTURE_WINDOW_MS;
}

/**
 * The customer panel reads the stored AI summary with a POST when it opens. A conversation with
 * no readable transcript (e.g. Instagram without text yet) answers 404; the card handles that
 * itself (hidden for the passive read, an inline note for a manual refresh), so no toast.
 */
export function isAiSummaryMiss(
  error: ApiError,
  request: { readonly method: string; readonly path: string },
): boolean {
  return (
    error.status === 404 &&
    request.method === "POST" &&
    /^\/v1\/workspaces\/[^/]+\/conversations\/[^/]+\/ai-summary$/.test(
      request.path.split("?")[0] ?? "",
    )
  );
}
